import type { ReleaseNoteEntry } from '../../dto/releaseNotes';

export interface ReleaseNoteDay {
  date: string;
  items: Array<{ key: string; text: string }>;
}

const TRAILING_PULL_REQUESTS = /\s*\(\s*#\d+(?:\s*,\s*#\d+)*\s*\)\s*$/;

/**
 * The changelog files end an item with its PR number(s) — ` (#123)` or
 * ` (#123, #124)` — for the GitHub Release. Business readers in the app do
 * not need them, so the display drops only that trailing reference.
 */
export function stripPullRequestReference(text: string): string {
  return text.replace(TRAILING_PULL_REQUESTS, '').trim();
}

/** Entries arrive newest first; consecutive entries of one day form one group. */
export function groupReleaseNotesByDate(entries: ReleaseNoteEntry[]): ReleaseNoteDay[] {
  const days: ReleaseNoteDay[] = [];
  for (const entry of entries) {
    let day = days[days.length - 1];
    if (!day || day.date !== entry.date) {
      day = { date: entry.date, items: [] };
      days.push(day);
    }
    entry.items.forEach((item, index) => {
      const text = stripPullRequestReference(item);
      if (text) day.items.push({ key: `${entry.slug}-${index}`, text });
    });
  }
  return days;
}

export type ReleaseNoteSegment = { text: string; bold: boolean };

/**
 * Splits an item into plain and `**bold**` segments. Everything else stays
 * literal text (rendered by React, so markup in the source is never HTML).
 */
export function splitReleaseNoteText(text: string): ReleaseNoteSegment[] {
  const segments: ReleaseNoteSegment[] = [];
  const pattern = /\*\*(.+?)\*\*/g;
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) segments.push({ text: text.slice(cursor, match.index), bold: false });
    segments.push({ text: match[1], bold: true });
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), bold: false });
  return segments;
}
